;(function () {
	'use strict'

	if (!customElements.get('l-crossroads-hero-video')) {
		class LTACrossroadsHeroVideo extends HTMLElement {
			constructor() {
				super()
				this.handleBreakpointChange = this.handleBreakpointChange.bind(this)
			}

			connectedCallback() {
				this.video = this.querySelector('video')
				this.sources = Array.from(this.querySelectorAll('source[data-src][data-l-crossroads-source-variant]'))
				if (!this.video || !this.sources.length) return

				this.breakpoint =
					typeof window.matchMedia === 'function'
						? window.matchMedia(this.dataset.mobileMedia || '(max-width: 767px)')
						: null
				this.syncSources()
				this.breakpoint?.addEventListener('change', this.handleBreakpointChange)
			}

			disconnectedCallback() {
				this.breakpoint?.removeEventListener('change', this.handleBreakpointChange)
				this.activeVariant = null
				this.breakpoint = null
				this.sources = null
				this.video = null
			}

			handleBreakpointChange() {
				this.syncSources()
			}

			syncSources() {
				if (!this.video?.isConnected || !this.sources?.length) return

				const hasUniversalSources = this.sources.some((source) => source.dataset.lCrossroadsSourceVariant === 'all')
				const nextVariant = hasUniversalSources ? 'all' : this.breakpoint?.matches ? 'mobile' : 'desktop'
				if (this.activeVariant === nextVariant) return

				const shouldResume = this.video.autoplay || !this.video.paused

				this.sources.forEach((source) => {
					if (source.dataset.lCrossroadsSourceVariant === nextVariant) {
						source.setAttribute('src', source.dataset.src)
					} else {
						source.removeAttribute('src')
					}
				})
				this.activeVariant = nextVariant
				this.video.load()
				if (!shouldResume) return

				const playPromise = this.video.play()
				if (playPromise?.catch) playPromise.catch(() => {})
			}
		}

		customElements.define('l-crossroads-hero-video', LTACrossroadsHeroVideo)
	}

	if (window.lCrossroadsMotion) return

	const SCOPE_SELECTOR = '[data-l-crossroads-reveal]'
	const TERMINAL_SCOPE_SELECTOR = '[data-l-crossroads-reveal-terminal]'
	const HEADING_SELECTOR = '[data-l-crossroads-reveal-heading]'
	const MEDIA_SELECTOR = '[data-l-crossroads-reveal-media]'
	const TEXT_SELECTOR = '[data-l-crossroads-reveal-text]'
	const CTA_SELECTOR = '[data-l-crossroads-reveal-cta]'
	const TARGET_SELECTOR = [HEADING_SELECTOR, MEDIA_SELECTOR, TEXT_SELECTOR, CTA_SELECTOR].join(',')
	const WAVE_SELECTOR = '[data-l-crossroads-wave]'
	const FLOAT_WORDS_SELECTOR = '[data-l-crossroads-float-words]'
	const CTA_RIGHT_SELECTOR = '[data-l-crossroads-reveal-cta-right]'
	const FADE_ONLY_SCOPE_SELECTOR = '[data-l-crossroads-reveal-fade-only]'
	const SIGNUP_SELECTOR = '[data-l-crossroads-signup-forms]'
	const DYNAMIC_SELECTOR = [SCOPE_SELECTOR, SIGNUP_SELECTOR].join(',')
	const REDUCED_MOTION = '(prefers-reduced-motion: reduce)'
	const CTA_RIGHT_ENTRANCE = 6
	const CENTERED_ENTRANCE_THRESHOLD = 128
	const MEDIA_ENTRANCE = { min: 120, max: 320, viewportRatio: 0.18, centeredRatio: 0.55 }
	const TEXT_ENTRANCE = { min: 48, max: 120, viewportRatio: 0.08, centeredRatio: 0.4 }

	const scopes = new Map()
	const signupObservers = new Map()
	const supportsMotion =
		!window.matchMedia?.(REDUCED_MOTION).matches && typeof window.IntersectionObserver === 'function' && window.gsap
	let motionEnabled = Boolean(supportsMotion)

	let entryObserver = null
	let terminalEntryObserver = null
	let dynamicObserver = null
	let dynamicFrame = null
	const dynamicRoots = new Set()

	const asArray = (collection) => Array.from(collection || [])

	const matchesOrFind = (root, selector) => {
		const matches = root instanceof Element && root.matches(selector) ? [root] : []

		return matches.concat(asArray(root.querySelectorAll?.(selector)))
	}

	const restoreSplit = (record) => {
		if (!record?.element) return

		record.element.innerHTML = record.html
		if (record.hadAriaLabel) {
			record.element.setAttribute('aria-label', record.ariaLabel)
		} else {
			record.element.removeAttribute('aria-label')
		}
	}

	const splitHeading = (element) => {
		const record = {
			element,
			html: element.innerHTML,
			hadAriaLabel: element.hasAttribute('aria-label'),
			ariaLabel: element.getAttribute('aria-label'),
			characters: [],
			words: [],
		}
		const labelSource = element.cloneNode(true)

		labelSource.querySelectorAll('br').forEach((breakElement) => breakElement.replaceWith(' '))
		const label = labelSource.textContent.replace(/\s+/g, ' ').trim()
		const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
			acceptNode(node) {
				return node.nodeValue ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT
			},
		})
		const textNodes = []

		while (walker.nextNode()) textNodes.push(walker.currentNode)

		textNodes.forEach((textNode) => {
			const fragment = document.createDocumentFragment()

			textNode.nodeValue.split(/(\s+)/).forEach((segment) => {
				if (!segment) return

				if (/^\s+$/.test(segment)) {
					fragment.appendChild(document.createTextNode(segment))

					return
				}

				const word = document.createElement('span')
				word.className = 'l-crossroads-word'
				word.setAttribute('aria-hidden', 'true')
				record.words.push(word)

				Array.from(segment).forEach((character) => {
					const span = document.createElement('span')
					span.className = 'l-crossroads-character'
					span.textContent = character
					word.appendChild(span)
					record.characters.push(span)
				})

				fragment.appendChild(word)
			})

			textNode.replaceWith(fragment)
		})

		if (label) element.setAttribute('aria-label', label)

		return record
	}

	const uniqueTopLevel = (elements) =>
		elements.filter(
			(element, index, list) =>
				list.indexOf(element) === index &&
				!list.some((candidate) => candidate !== element && candidate.contains(element))
		)

	const getEntranceOffset = (element, scope, entrance) => {
		const elementRect = element.getBoundingClientRect()
		const scopeRect = scope.getBoundingClientRect()
		const elementCenter = elementRect.left + elementRect.width / 2
		const scopeCenter = scopeRect.left + scopeRect.width / 2
		const centeredThreshold = Math.min(scopeRect.width * 0.1, CENTERED_ENTRANCE_THRESHOLD)
		const delta = elementCenter - scopeCenter
		const distance = Math.min(entrance.max, Math.max(entrance.min, scopeRect.width * entrance.viewportRatio))

		if (!elementRect.width || !scopeRect.width || Math.abs(delta) <= centeredThreshold) {
			return { x: 0, y: Math.round(distance * entrance.centeredRatio) }
		}

		return { x: delta < 0 ? -distance : distance, y: 0 }
	}
	const clearMotionStyles = (state) => {
		const elements = (state.groups?.allTargets || [])
			.concat(state.groups?.characters || [])
			.concat(state.groups?.floatingWords || [])
			.concat(state.scope)

		if (window.gsap) {
			window.gsap.set(elements, {
				clearProps: 'opacity,transform,visibility,willChange',
			})
		}

		state.scope.classList.remove('is-l-crossroads-reveal-bound')
		state.scope.classList.add('is-l-crossroads-revealed')
	}

	const revealImmediately = (root) => {
		matchesOrFind(root, SCOPE_SELECTOR).forEach((scope) => {
			scope.classList.remove('is-l-crossroads-reveal-bound')
			scope.classList.add('is-l-crossroads-revealed')
			asArray(scope.querySelectorAll(TARGET_SELECTOR)).forEach((element) => {
				element.style.removeProperty('opacity')
				element.style.removeProperty('transform')
				element.style.removeProperty('visibility')
				element.style.removeProperty('will-change')
			})
		})

		document.documentElement.classList.remove('l-crossroads-reveal-prep')
	}

	const prepareScope = (scope) => {
		if (scopes.has(scope)) return
		scope.classList.add('is-l-crossroads-reveal-bound')

		const state = {
			scope,
			timeline: null,
			splits: [],
			groups: null,
		}
		const withinScope = (selector) =>
			asArray(scope.querySelectorAll(selector)).filter((element) => element.closest(SCOPE_SELECTOR) === scope)
		const allTargets = withinScope(TARGET_SELECTOR)
		const headings = withinScope(HEADING_SELECTOR)
		const media = uniqueTopLevel(withinScope(MEDIA_SELECTOR))
		const text = uniqueTopLevel(withinScope(TEXT_SELECTOR))
		const ctas = uniqueTopLevel(withinScope(CTA_SELECTOR))
		const fadeOnly = scope.matches(FADE_ONLY_SCOPE_SELECTOR)

		headings
			.filter((heading) => heading.matches(`${WAVE_SELECTOR}, ${FLOAT_WORDS_SELECTOR}`))
			.forEach((heading) => state.splits.push(splitHeading(heading)))

		const splitHeadings = new Set(state.splits.map((split) => split.element))
		const plainHeadings = headings.filter((heading) => !splitHeadings.has(heading))
		const characters = state.splits
			.filter((split) => split.element.matches(WAVE_SELECTOR))
			.flatMap((split) => split.characters)
		const floatingWords = state.splits
			.filter((split) => split.element.matches(FLOAT_WORDS_SELECTOR))
			.flatMap((split) => split.words)
		const rotateCharacters = state.splits
			.filter((split) => split.element.hasAttribute('data-l-crossroads-rotate-x'))
			.flatMap((split) => split.characters)
		const standardCtas = ctas.filter((cta) => !cta.matches(CTA_RIGHT_SELECTOR))
		const rightCtas = ctas.filter((cta) => cta.matches(CTA_RIGHT_SELECTOR))

		const mediaOffsets = new Map(media.map((element) => [element, getEntranceOffset(element, scope, MEDIA_ENTRANCE)]))
		const textOffsets = fadeOnly
			? new Map()
			: new Map(text.map((element) => [element, getEntranceOffset(element, scope, TEXT_ENTRANCE)]))
		const rightCtaOffsets = new Map(rightCtas.map((element) => [element, CTA_RIGHT_ENTRANCE]))

		state.groups = {
			allTargets,
			media,
			text,
			standardCtas,
			rightCtas,
			plainHeadings,
			characters,
			floatingWords,
			rotateCharacters,
			mediaOffsets,
			textOffsets,
			rightCtaOffsets,
			fadeOnly,
		}
		scopes.set(scope, state)

		window.gsap.set(allTargets, { visibility: 'visible' })
		window.gsap.set(media, {
			autoAlpha: 0.12,
			x: (_index, element) => mediaOffsets.get(element)?.x || 0,
			y: (_index, element) => mediaOffsets.get(element)?.y || 0,
			scale: 1.012,
			willChange: 'transform,opacity',
		})
		window.gsap.set(plainHeadings, { autoAlpha: 0, y: 16, willChange: 'transform,opacity' })
		window.gsap.set(
			text,
			fadeOnly
				? { autoAlpha: 0, willChange: 'opacity' }
				: {
						autoAlpha: 0,
						x: (_index, element) => textOffsets.get(element)?.x || 0,
						y: (_index, element) => textOffsets.get(element)?.y || 0,
						willChange: 'transform,opacity',
					}
		)
		window.gsap.set(standardCtas, { autoAlpha: 0, y: 10, willChange: 'transform,opacity' })
		window.gsap.set(rightCtas, {
			autoAlpha: 0,
			x: (_index, element) => rightCtaOffsets.get(element) || CTA_RIGHT_ENTRANCE,
			willChange: 'transform,opacity',
		})
		window.gsap.set(characters, { autoAlpha: 0, yPercent: 32, willChange: 'transform,opacity' })
		window.gsap.set(floatingWords, { autoAlpha: 0, yPercent: 65, willChange: 'transform,opacity' })
		window.gsap.set(rotateCharacters, { rotateX: -8, transformPerspective: 600 })

		const observer = scope.matches(TERMINAL_SCOPE_SELECTOR) ? terminalEntryObserver : entryObserver

		observer.observe(scope)
	}

	const animateScope = (scope) => {
		const state = scopes.get(scope)
		if (!state || state.timeline) return

		const { media, text, standardCtas, rightCtas, plainHeadings, characters, floatingWords, fadeOnly } = state.groups
		const timeline = window.gsap.timeline({
			defaults: { duration: 0.68, ease: 'power2.out' },
			onStart: () => scope.classList.add('is-l-crossroads-revealed'),
			onComplete: () => clearMotionStyles(state),
		})

		state.timeline = timeline
		timeline.to(media, { autoAlpha: 1, x: 0, y: 0, scale: 1, duration: 0.82, ease: 'power3.out', stagger: 0.06 }, 0)
		timeline.to(plainHeadings, { autoAlpha: 1, y: 0, stagger: 0.05 }, 0.08)
		timeline.to(floatingWords, { autoAlpha: 1, yPercent: 0, duration: 0.8, ease: 'power3.out', stagger: 0.055 }, 0.08)
		if (characters.length) {
			timeline.to(
				characters,
				{
					autoAlpha: 1,
					yPercent: 0,
					rotateX: 0,
					duration: 0.6,
					stagger: 0.018,
				},
				0.08
			)
		}
		timeline.to(
			text,
			fadeOnly ? { autoAlpha: 1, duration: 0.55, ease: 'power1.out' } : { autoAlpha: 1, x: 0, y: 0, stagger: 0.06 },
			fadeOnly ? 0 : 0.16
		)
		timeline.to(standardCtas, { autoAlpha: 1, y: 0, stagger: 0.05 }, 0.24)
		timeline.to(rightCtas, { autoAlpha: 1, x: 0, duration: 1.05, ease: 'power3.out', stagger: 0.05 }, 0.38)
	}

	const setupEntryObserver = () => {
		const revealEntries = (entries, observer) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) return

				observer.unobserve(entry.target)
				try {
					animateScope(entry.target)
				} catch (error) {
					failOpen(error)
				}
			})
		}

		entryObserver = new IntersectionObserver(revealEntries, {
			rootMargin: '0px 0px -12% 0px',
			threshold: 0.08,
		})
		// A terminal scope cannot enter a bottom-inset root once the page reaches maximum scroll.
		terminalEntryObserver = new IntersectionObserver(revealEntries, { threshold: 0.08 })
	}

	const setupSignup = (container) => {
		if (signupObservers.has(container)) return

		const klaviyo = container.querySelector('[data-l-crossroads-klaviyo]')
		if (!klaviyo || typeof MutationObserver !== 'function') return

		const update = () => {
			container.classList.toggle('is-klaviyo-ready', klaviyo.childElementCount > 0)
		}
		const observer = new MutationObserver(update)

		observer.observe(klaviyo, { childList: true, subtree: true })
		signupObservers.set(container, observer)
		update()
	}

	const initRoot = (root) => {
		if (!root?.querySelectorAll) return

		scopes.forEach((_state, scope) => {
			if (!scope.isConnected) cleanupScope(scope)
		})
		signupObservers.forEach((observer, container) => {
			if (container.isConnected) return
			observer.disconnect()
			signupObservers.delete(container)
		})

		if (!motionEnabled) {
			revealImmediately(root)
		} else {
			matchesOrFind(root, SCOPE_SELECTOR).forEach(prepareScope)
		}

		matchesOrFind(root, SIGNUP_SELECTOR).forEach(setupSignup)
	}

	const cleanupScope = (scope) => {
		const state = scopes.get(scope)
		if (!state) return

		entryObserver?.unobserve(scope)
		terminalEntryObserver?.unobserve(scope)
		state.timeline?.kill()
		clearMotionStyles(state)
		state.splits.forEach(restoreSplit)
		scopes.delete(scope)
	}

	const cleanupRoot = (root) => {
		matchesOrFind(root, SCOPE_SELECTOR).forEach(cleanupScope)
		matchesOrFind(root, SIGNUP_SELECTOR).forEach((container) => {
			signupObservers.get(container)?.disconnect()
			signupObservers.delete(container)
			container.classList.remove('is-klaviyo-ready')
		})
	}

	const reinitializeRoot = (root) => {
		cleanupRoot(root)
		initRoot(root)
	}

	const eventRoot = (event) => event.detail?.container || event.detail?.element || event.target || document
	const flushDynamicRoots = () => {
		dynamicFrame = null
		const roots = uniqueTopLevel(asArray(dynamicRoots)).filter((root) => root.isConnected)

		dynamicRoots.clear()
		roots.forEach(safelyInitRoot)
	}
	const queueDynamicRoot = (root) => {
		dynamicRoots.add(root)
		if (dynamicFrame !== null) return

		if (typeof window.requestAnimationFrame === 'function') {
			dynamicFrame = window.requestAnimationFrame(flushDynamicRoots)
		} else {
			dynamicFrame = window.setTimeout(flushDynamicRoots, 0)
		}
	}
	const setupDynamicObserver = () => {
		if (dynamicObserver || typeof MutationObserver !== 'function' || !document.body) return

		dynamicObserver = new MutationObserver((records) => {
			records.forEach((record) => {
				asArray(record.addedNodes).forEach((node) => {
					if (!(node instanceof Element)) return
					if (!node.matches(DYNAMIC_SELECTOR) && !node.querySelector(DYNAMIC_SELECTOR)) return

					queueDynamicRoot(node)
				})
			})
		})

		dynamicObserver.observe(document.body, { childList: true, subtree: true })
	}
	const deferDocumentInit = () => {
		window.setTimeout(() => {
			if (typeof window.requestAnimationFrame === 'function') {
				window.requestAnimationFrame(() => safelyInitRoot(document))
			} else {
				safelyInitRoot(document)
			}
		}, 0)
	}
	const failOpen = (error) => {
		motionEnabled = false
		window.__lCrossroadsRevealActive = false
		entryObserver?.disconnect()
		terminalEntryObserver?.disconnect()
		dynamicObserver?.disconnect()
		entryObserver = null
		terminalEntryObserver = null
		dynamicObserver = null
		if (dynamicFrame !== null) {
			if (typeof window.cancelAnimationFrame === 'function') {
				window.cancelAnimationFrame(dynamicFrame)
			} else {
				window.clearTimeout(dynamicFrame)
			}
			dynamicFrame = null
		}
		dynamicRoots.clear()

		scopes.forEach((state) => {
			try {
				state.timeline?.kill()
			} catch (_error) {
				// Continue opening the page even if a partially-created timeline cannot be killed.
			}
			state.splits.forEach((record) => {
				try {
					restoreSplit(record)
				} catch (_error) {
					// The remaining fail-open cleanup still restores visibility.
				}
			})
		})
		scopes.clear()
		revealImmediately(document)

		if (window.console?.error) window.console.error('Crossroads motion initialization failed.', error)
	}
	const safelyInitRoot = (root) => {
		try {
			initRoot(root)
		} catch (error) {
			failOpen(error)
		}
	}

	// Scopes are prepared at DOMContentLoaded, before hero media has loaded. An image with
	// no intrinsic size yet leaves its scope near zero-height, so the first
	// IntersectionObserver sample can miss it and nothing above the fold ever animates in —
	// the visitor sees hidden headings and washed-out media until a scroll changes the
	// geometry. Re-check the viewport once `load` has settled layout, and again shortly
	// after for media that resolves late. Only scopes actually on screen are revealed;
	// everything below the fold keeps its normal scroll-triggered entrance.
	const revealVisibleScopes = () => {
		if (!motionEnabled) return

		const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0

		scopes.forEach((state, scope) => {
			if (state.timeline || !scope.isConnected) return

			const rect = scope.getBoundingClientRect()
			if (rect.top >= viewportHeight || rect.bottom <= 0) return

			entryObserver?.unobserve(scope)
			terminalEntryObserver?.unobserve(scope)

			try {
				animateScope(scope)
			} catch (error) {
				failOpen(error)
			}
		})
	}

	// Above-fold content must never wait on the network. `load` does not fire until every image
	// on the page has finished, which on these pages is seconds — long enough for the first
	// screen to sit ghosted even though its own markup was ready immediately. So the first sweep
	// runs synchronously at boot, a second on the next frame once first layout has settled, and
	// the later ones only mop up media that resolves late. The final pass is a backstop: if the
	// observer never fired and nothing else caught it, content still opens rather than stranding.
	const scheduleVisibleScopeSweep = () => {
		revealVisibleScopes()

		if (typeof window.requestAnimationFrame === 'function') {
			window.requestAnimationFrame(revealVisibleScopes)
		}

		if (document.readyState === 'complete') {
			window.setTimeout(revealVisibleScopes, 600)
		} else {
			window.addEventListener(
				'load',
				() => {
					revealVisibleScopes()
					window.setTimeout(revealVisibleScopes, 600)
				},
				{ once: true }
			)
		}

		window.setTimeout(revealVisibleScopes, 2000)
	}

	const boot = () => {
		try {
			if (motionEnabled) setupEntryObserver()
			initRoot(document)
			setupDynamicObserver()
			document.documentElement.classList.remove('l-crossroads-reveal-prep')
			window.__lCrossroadsRevealActive = motionEnabled

			scheduleVisibleScopeSweep()
		} catch (error) {
			failOpen(error)
		}
	}

	document.addEventListener('shopify:section:load', (event) => safelyInitRoot(eventRoot(event)))
	document.addEventListener('shopify:section:unload', (event) => cleanupRoot(eventRoot(event)))
	document.addEventListener('shopify:section:reorder', (event) => {
		try {
			reinitializeRoot(eventRoot(event))
		} catch (error) {
			failOpen(error)
		}
	})
	document.addEventListener('shopify:block:select', (event) => safelyInitRoot(eventRoot(event)))
	document.addEventListener('l:filters:rendered', deferDocumentInit)
	document.addEventListener('l:product-recommendations:rendered', deferDocumentInit)
	document.addEventListener('product-recommendations:loaded', deferDocumentInit)
	document.addEventListener('shopify:recommendations:load', deferDocumentInit)

	window.lCrossroadsMotion = Object.freeze({ initRoot: safelyInitRoot, cleanupRoot })

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', boot, { once: true })
	} else {
		boot()
	}
})()
